/**
 * Compass Item entity — a collectible compass that appears on the maze floor.
 *
 * When collected, emits a COMPASS_COLLECTED event so the HUD can display the
 * compass indicator pointing toward the exit. The compass spawns once per level
 * with a configurable probability (COMPASS_SPAWN_CHANCE).
 *
 * Visual: uses the TEX_COMPASS procedural texture, with a gentle float and
 * periodic glint to attract the player's attention.
 *
 * @extends Phaser.GameObjects.Sprite
 */

import Phaser from 'phaser';
import { TEX_COMPASS, TEX_GLOW } from '../utils/constants.js';
import {
  TILE_SIZE,
  AUDIO_COMPASS_COLLECT,
  COMPASS_GLINT_INTERVAL_MIN,
  COMPASS_GLINT_INTERVAL_MAX,
  COMPASS_GLINT_DURATION,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class CompassItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene - The scene this compass belongs to.
   * @param {number} col - Grid column (expanded grid).
   * @param {number} row - Grid row (expanded grid).
   */
  constructor(scene, col, row) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    super(scene, x, y, TEX_COMPASS);

    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {number} Grid column in the expanded grid. */
    this.gridCol = col;

    /** @type {number} Grid row in the expanded grid. */
    this.gridRow = row;

    scene.add.existing(this);
    this.setDepth(5);
    this.setScale(0);

    // --- Pop-in animation ---
    scene.tweens.add({
      targets: this,
      scale: 1.2,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        scene.tweens.add({
          targets: this,
          scale: 1,
          duration: 150,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // --- Gentle float (up/down) ---
    this._floatTween = scene.tweens.add({
      targets: this,
      y: y - 2,
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Slow rotation (compass dial feel) ---
    this._rotateTween = scene.tweens.add({
      targets: this,
      angle: { from: -8, to: 8 },
      duration: 2000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Glow halo behind the compass ---
    this._glow = scene.add.graphics();
    this._glow.setDepth(4);
    this._glowAlpha = 0.25;
    this._drawGlow();

    this._glowTween = scene.tweens.add({
      targets: this,
      _glowAlpha: 0.08,
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        this._drawGlow();
      },
    });

    // --- Periodic glint ---
    this._scheduleGlint();
  }

  /**
   * Draw the soft glow halo behind the compass.
   * @private
   */
  _drawGlow() {
    if (!this._glow || !this.scene) return;
    this._glow.clear();
    this._glow.fillStyle(0xffffff, this._glowAlpha);
    this._glow.fillCircle(this.x, this.y, 12);
  }

  /**
   * Schedule the next random glint flash.
   * @private
   */
  _scheduleGlint() {
    if (!this.scene) return;
    const delay = Phaser.Math.Between(COMPASS_GLINT_INTERVAL_MIN, COMPASS_GLINT_INTERVAL_MAX);
    this._glintTimer = this.scene.time.delayedCall(delay, () => {
      this._playGlint();
    });
  }

  /**
   * Flash a brief white glint on the compass.
   * @private
   */
  _playGlint() {
    if (!this.scene || !this.active) return;

    // Quick alpha flash
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 1, to: 0.3 },
      duration: COMPASS_GLINT_DURATION / 2,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // Particle burst
    const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
      lifespan: 400,
      speed: { min: 15, max: 40 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.8, end: 0 },
      frequency: -1,
    });
    emitter.setDepth(6);
    emitter.explode(4);
    this.scene.time.delayedCall(500, () => {
      if (emitter) emitter.destroy();
    });

    this._scheduleGlint();
  }

  /**
   * Collect the compass. Plays sound and a collection animation.
   * The caller is responsible for emitting the COMPASS_COLLECTED event.
   */
  collect() {
    if (!this.active) return;
    this.active = false;

    // Cancel timers
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }

    if (!this.scene) return;

    // Play collection sound
    try {
      this.scene.sound.play(AUDIO_COMPASS_COLLECT, { volume: getSfxVol(0.6) });
    } catch (_) { /* audio fallback */ }

    // Show collect label
    this._showCollectLabel();

    // Animate collection: scale up + fade
    this.scene.tweens.add({
      targets: this,
      scale: 2.5,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  /**
   * Show a floating "+COMPASS" label above the collection point.
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    const label = this.scene.add.text(this.x, this.y - 10, 'COMPASS', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0.5)
      .setDepth(150)
      .setScale(0.5);

    this.scene.tweens.add({
      targets: label,
      scale: 2.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!label.scene) return;
        label.scene.tweens.add({
          targets: label,
          y: label.y - 30,
          alpha: 0,
          duration: 600,
          delay: 300,
          ease: 'Quad.easeOut',
          onComplete: () => {
            label.destroy();
          },
        });
      },
    });
  }

  /**
   * Clean up resources.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    if (this._rotateTween) {
      this._rotateTween.stop();
      this._rotateTween = null;
    }
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
    super.destroy(fromScene);
  }
}
